// frontend/src/components/staff/CorrectionRequestQueue.js
//
// Workforce — TIME-010 §4 Admin Correction Request Queue.
//
// Renders as the body of the new "Pending Corrections (N)" tab on the
// existing TIME-009 Daily Review dashboard. Surfaces are:
//   - §4.2.1 filter row (status dropdown, staff-name search, date range)
//   - §4.2.2 table columns (Staff, Submitted, Target date, Intended times,
//                           Reason, Actions)
//   - §4.2.3 empty / loading / error states
//   - §4.4 row-detail drawer (read-only review surface) — invoked via
//          onOpenDetail callback
//   - §6.1 + §6.2 Approve / Reject confirmation modals
//
// Backend contract per Marcus tech doc §4.2 + §4.3 + §4.4 captured-curl
// JSON. Client-side join staff names against the parallel
// staffProfileAPI.list response per §4.3.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import moment from "moment-timezone";
import {
  timelogCorrectionAPI,
  staffProfileAPI,
} from "../../services/api";
import "./CorrectionRequestQueue.css";

const STUDIO_TZ = "America/Phoenix";
const POLL_INTERVAL_MS = 30_000;
const SEARCH_DEBOUNCE_MS = 200;
const DECISION_REASON_MIN = 10;
const DECISION_REASON_MAX = 500;

const STATUS = {
  PENDING: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  WITHDRAWN: "Withdrawn",
  ALL: "All",
};

const formatRelative = (iso) => {
  if (!iso) return "—";
  const m = moment.tz(iso, STUDIO_TZ);
  if (!m.isValid()) return "—";
  return m.fromNow();
};

const formatStudioDateTime = (iso) => {
  if (!iso) return "";
  const m = moment.tz(iso, STUDIO_TZ);
  if (!m.isValid()) return "";
  return `${m.format("YYYY-MM-DD h:mm A")} MST`;
};

const formatTargetDate = (iso) => {
  if (!iso) return { primary: "—", secondary: "" };
  const m = moment.tz(iso, STUDIO_TZ);
  if (!m.isValid()) return { primary: "—", secondary: "" };
  return {
    primary: m.format("MMM D, YYYY"),
    secondary: m.format("dddd"),
  };
};

const formatIntendedTimes = (inIso, outIso, isCorrection) => {
  if (!inIso) return "—";
  const inM = moment.tz(inIso, STUDIO_TZ);
  const outM = outIso ? moment.tz(outIso, STUDIO_TZ) : null;
  const tag = isCorrection ? " (correction)" : "";
  const inLabel = inM.format("h:mm A");
  const outLabel = outM ? outM.format("h:mm A") : "(still working)";
  return `${inLabel} – ${outLabel} MST${tag}`;
};

const truncate = (s, n) => {
  if (!s) return "";
  if (s.length <= n) return s;
  return `${s.slice(0, n - 1)}…`;
};

/**
 * Approve confirmation modal — Zuri spec §6.1.
 * Optional decisionReason. Submit fires timelogCorrectionAPI.approve.
 */
const ApproveModal = ({
  open,
  request,
  staffName,
  submitting,
  bannerError,
  onCancel,
  onSubmit,
}) => {
  const [note, setNote] = useState("");
  useEffect(() => {
    if (open) setNote("");
  }, [open]);
  if (!open || !request) return null;
  const isCorrection = Boolean(request.targetTimeLogId);
  const targetDate = moment
    .tz(request.targetDate, STUDIO_TZ)
    .format("MMM D, YYYY");
  const inLabel = moment
    .tz(request.intendedPunchInTime, STUDIO_TZ)
    .format("h:mm A");
  const outLabel = request.intendedPunchOutTime
    ? moment.tz(request.intendedPunchOutTime, STUDIO_TZ).format("h:mm A")
    : "(still working)";
  const subhead = isCorrection
    ? `This will update ${staffName || "the staff member"}'s existing TimeLog for ${targetDate}.`
    : `This will create a new TimeLog for ${staffName || "the staff member"} on ${targetDate}.`;
  return (
    <>
      <div className="crq-backdrop" aria-hidden="true" />
      <div
        className="crq-confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="crq-approve-heading"
        data-testid="crq-approve-modal"
      >
        <header className="crq-confirm-header">
          <h2 id="crq-approve-heading" className="crq-confirm-heading">
            Approve correction request?
          </h2>
          <p className="crq-confirm-subhead">{subhead}</p>
        </header>
        {bannerError && (
          <div
            className={`crq-banner crq-banner-${bannerError.accent || "error"}`}
            role="alert"
          >
            {bannerError.message}
          </div>
        )}
        <div className="crq-confirm-body">
          <dl className="crq-summary">
            <dt>Target date:</dt>
            <dd>{targetDate}</dd>
            <dt>Intended:</dt>
            <dd>{`${inLabel} – ${outLabel} MST`}</dd>
            <dt>Reason:</dt>
            <dd>
              <em>&ldquo;{request.reason}&rdquo;</em>
            </dd>
          </dl>
          <label className="crq-confirm-label" htmlFor="crq-approve-note">
            Decision note (optional)
          </label>
          <textarea
            id="crq-approve-note"
            className="crq-confirm-textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={DECISION_REASON_MAX + 100}
            placeholder="Add a note for the audit trail (optional)..."
            data-testid="crq-approve-note"
          />
          <div className="crq-counter-row">
            <span
              className={
                note.length > DECISION_REASON_MAX
                  ? "crq-counter crq-counter-over"
                  : "crq-counter"
              }
            >
              {note.length} / {DECISION_REASON_MAX}
            </span>
          </div>
        </div>
        <footer className="crq-confirm-footer">
          <button
            type="button"
            className="crq-btn crq-btn-secondary"
            onClick={onCancel}
            disabled={submitting}
            data-testid="crq-approve-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="crq-btn crq-btn-primary"
            onClick={() =>
              onSubmit({ decisionReason: note.trim() || undefined })
            }
            disabled={submitting || note.length > DECISION_REASON_MAX}
            data-testid="crq-approve-submit"
          >
            {submitting ? "Approving..." : "Approve"}
          </button>
        </footer>
      </div>
    </>
  );
};

/**
 * Reject reason modal — Zuri spec §6.2 (mandatory-reason variant; NO X icon;
 * Reject button is red destructive per documented divergence from
 * design-system §4 convention).
 */
const RejectModal = ({
  open,
  request,
  staffName,
  submitting,
  bannerError,
  onCancel,
  onSubmit,
}) => {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (open) {
      setReason("");
      setTouched(false);
    }
  }, [open]);
  if (!open || !request) return null;
  const trimmed = reason.trim();
  const showInlineError =
    touched &&
    (trimmed.length < DECISION_REASON_MIN || reason.length > DECISION_REASON_MAX);
  const canSubmit =
    !submitting &&
    trimmed.length >= DECISION_REASON_MIN &&
    reason.length <= DECISION_REASON_MAX;
  const targetDate = moment
    .tz(request.targetDate, STUDIO_TZ)
    .format("MMM D, YYYY");
  return (
    <>
      <div className="crq-backdrop" aria-hidden="true" />
      <div
        className="crq-confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="crq-reject-heading"
        data-testid="crq-reject-modal"
      >
        <header className="crq-confirm-header">
          <h2 id="crq-reject-heading" className="crq-confirm-heading">
            Reject correction request?
          </h2>
          <p className="crq-confirm-subhead">
            Tell {staffName || "the staff member"} why. They&apos;ll see the
            reason on their dashboard.
          </p>
        </header>
        {bannerError && (
          <div
            className={`crq-banner crq-banner-${bannerError.accent || "error"}`}
            role="alert"
          >
            {bannerError.message}
          </div>
        )}
        <div className="crq-confirm-body">
          <dl className="crq-summary">
            <dt>Target date:</dt>
            <dd>{targetDate}</dd>
            <dt>Reason:</dt>
            <dd>
              <em>&ldquo;{request.reason}&rdquo;</em>
            </dd>
          </dl>
          <label className="crq-confirm-label" htmlFor="crq-reject-reason">
            Decision reason <span className="crq-required">*</span>
          </label>
          <textarea
            id="crq-reject-reason"
            className="crq-confirm-textarea"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            maxLength={DECISION_REASON_MAX + 100}
            placeholder=""
            aria-required="true"
            aria-describedby="crq-reject-help crq-reject-counter"
            data-testid="crq-reject-reason"
          />
          <div className="crq-counter-row">
            <span id="crq-reject-help" className="crq-confirm-help">
              Required. At least {DECISION_REASON_MIN} characters. Visible to
              the staff member.
            </span>
            <span
              id="crq-reject-counter"
              className={
                reason.length > DECISION_REASON_MAX
                  ? "crq-counter crq-counter-over"
                  : "crq-counter"
              }
              aria-live="polite"
            >
              {reason.length} / {DECISION_REASON_MAX}
            </span>
          </div>
          {showInlineError && (
            <p className="crq-field-error" role="alert">
              {trimmed.length < DECISION_REASON_MIN
                ? `Reason must be at least ${DECISION_REASON_MIN} characters.`
                : `Reason cannot exceed ${DECISION_REASON_MAX} characters.`}
            </p>
          )}
        </div>
        <footer className="crq-confirm-footer">
          <button
            type="button"
            className="crq-btn crq-btn-secondary"
            onClick={onCancel}
            disabled={submitting}
            data-testid="crq-reject-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="crq-btn crq-btn-destructive"
            onClick={() => onSubmit({ decisionReason: trimmed })}
            disabled={!canSubmit}
            data-testid="crq-reject-submit"
          >
            {submitting ? "Rejecting..." : "Reject"}
          </button>
        </footer>
      </div>
    </>
  );
};

const mapApproveRejectError = (err) => {
  const data = err?.response?.data || {};
  const code = data.error || "server_error";
  switch (code) {
    case "period_locked":
      return {
        accent: "locked",
        message:
          "Period is locked. Reopen via PAY-006 (coming soon) to act on this request.",
      };
    case "invalid_transition":
      return {
        accent: "neutral",
        message:
          "This request was already acted on by another admin. Refresh to see current state.",
      };
    case "decision_reason_required":
      return {
        accent: "error",
        message: `Decision reason must be at least ${DECISION_REASON_MIN} characters.`,
      };
    case "decision_reason_too_long":
      return {
        accent: "error",
        message: `Decision reason can be at most ${DECISION_REASON_MAX} characters.`,
      };
    case "correction_request_not_found":
      return {
        accent: "error",
        message: "This request no longer exists. Refresh.",
      };
    default:
      return {
        accent: "error",
        message: "Couldn't complete the action. Refresh and try again.",
      };
  }
};

const CorrectionRequestQueue = ({ onOpenDetail, onActionToast }) => {
  const [requests, setRequests] = useState([]);
  const [staffById, setStaffById] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [statusFilter, setStatusFilter] = useState(STATUS.PENDING);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateFilter, setDateFilter] = useState({ from: "", to: "", expanded: false });
  const [approveModal, setApproveModal] = useState({
    open: false,
    request: null,
    submitting: false,
    bannerError: null,
  });
  const [rejectModal, setRejectModal] = useState({
    open: false,
    request: null,
    submitting: false,
    bannerError: null,
  });
  const intervalRef = useRef(null);

  // Debounce search input — Zuri §4.2.1 specifies 200ms client-side filter.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const fetchRequests = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== STATUS.ALL) params.status = statusFilter;
      if (dateFilter.from) params.dateFrom = dateFilter.from;
      if (dateFilter.to) params.dateTo = dateFilter.to;

      const [correctionsRes, staffRes] = await Promise.all([
        timelogCorrectionAPI.list(params),
        // Refetch staff list once on first load only; cached otherwise.
        staffById.size === 0
          ? staffProfileAPI.list({ active: true })
          : Promise.resolve(null),
      ]);

      const corrections = correctionsRes?.data?.corrections || [];
      setRequests(Array.isArray(corrections) ? corrections : []);

      if (staffRes && staffRes.data) {
        const list =
          (Array.isArray(staffRes.data?.staffProfiles) &&
            staffRes.data.staffProfiles) ||
          (Array.isArray(staffRes.data) && staffRes.data) ||
          (Array.isArray(staffRes.data?.staff) && staffRes.data.staff) ||
          [];
        const map = new Map();
        list.forEach((p) => {
          const id = p._id || p.id;
          const userObj =
            (p.user && typeof p.user === "object" && p.user) ||
            (p.userId && typeof p.userId === "object" && p.userId) ||
            null;
          const firstName = userObj?.firstName || p.firstName || "";
          const lastName = userObj?.lastName || p.lastName || "";
          const name = `${firstName} ${lastName}`.trim() || userObj?.email || "(unknown)";
          map.set(id, {
            name,
            role: p.primaryRole || p.role || "",
          });
        });
        setStaffById(map);
      }
      setError(false);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFilter.from, dateFilter.to, staffById.size]);

  useEffect(() => {
    fetchRequests();
    intervalRef.current = setInterval(fetchRequests, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchRequests]);

  // Client-side join + filter pass.
  const enrichedRequests = useMemo(() => {
    return requests.map((r) => {
      const staff = staffById.get(r.requestedBy);
      return {
        ...r,
        staffName: staff?.name || "Unknown staff",
        staffRole: staff?.role || "",
      };
    });
  }, [requests, staffById]);

  const filteredRequests = useMemo(() => {
    if (!debouncedSearch) return enrichedRequests;
    const needle = debouncedSearch.toLowerCase();
    return enrichedRequests.filter((r) =>
      (r.staffName || "").toLowerCase().includes(needle)
    );
  }, [enrichedRequests, debouncedSearch]);

  // Sort by createdAt DESC (newest first).
  const sortedRequests = useMemo(
    () =>
      [...filteredRequests].sort((a, b) => {
        const ta = new Date(a.createdAt).getTime();
        const tb = new Date(b.createdAt).getTime();
        return tb - ta;
      }),
    [filteredRequests]
  );

  const openApproveModal = (request) =>
    setApproveModal({ open: true, request, submitting: false, bannerError: null });
  const openRejectModal = (request) =>
    setRejectModal({ open: true, request, submitting: false, bannerError: null });

  const closeApproveModal = () =>
    setApproveModal({ open: false, request: null, submitting: false, bannerError: null });
  const closeRejectModal = () =>
    setRejectModal({ open: false, request: null, submitting: false, bannerError: null });

  const handleApprove = async (payload) => {
    if (!approveModal.request) return;
    setApproveModal((prev) => ({ ...prev, submitting: true, bannerError: null }));
    try {
      const res = await timelogCorrectionAPI.approve(
        approveModal.request._id,
        payload
      );
      const resultingTimeLogId = res?.data?.correctionRequest?.resultingTimeLogId;
      closeApproveModal();
      fetchRequests();
      if (typeof onActionToast === "function") {
        onActionToast({
          kind: "success",
          message: "Request approved. TimeLog created/updated.",
          link: resultingTimeLogId
            ? { label: "View →", href: `#timelog-${resultingTimeLogId}` }
            : null,
        });
      }
    } catch (err) {
      const mapping = mapApproveRejectError(err);
      setApproveModal((prev) => ({
        ...prev,
        submitting: false,
        bannerError: mapping,
      }));
    }
  };

  const handleReject = async (payload) => {
    if (!rejectModal.request) return;
    setRejectModal((prev) => ({ ...prev, submitting: true, bannerError: null }));
    try {
      await timelogCorrectionAPI.reject(rejectModal.request._id, payload);
      closeRejectModal();
      fetchRequests();
      if (typeof onActionToast === "function") {
        onActionToast({ kind: "success", message: "Request rejected." });
      }
    } catch (err) {
      const mapping = mapApproveRejectError(err);
      setRejectModal((prev) => ({
        ...prev,
        submitting: false,
        bannerError: mapping,
      }));
    }
  };

  const handleRowClick = (request, e) => {
    // Ignore clicks bubbled from action buttons.
    if (e.target?.closest?.("[data-no-row-click]")) return;
    if (typeof onOpenDetail === "function") onOpenDetail(request);
  };

  return (
    <div className="crq-container" data-testid="crq-container">
      {/* Filter row */}
      <div className="crq-filters">
        <label className="crq-filter">
          <span className="crq-filter-label">Status:</span>
          <select
            className="crq-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="crq-status-filter"
          >
            <option value={STATUS.PENDING}>Pending</option>
            <option value={STATUS.APPROVED}>Approved</option>
            <option value={STATUS.REJECTED}>Rejected</option>
            <option value={STATUS.ALL}>All</option>
          </select>
        </label>
        <label className="crq-filter">
          <span className="crq-filter-label">Search:</span>
          <input
            type="search"
            className="crq-filter-search"
            placeholder="staff name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="crq-search"
            aria-label="Search by staff name"
          />
        </label>
        <button
          type="button"
          className="crq-btn-link"
          onClick={() =>
            setDateFilter((d) => ({ ...d, expanded: !d.expanded }))
          }
          aria-expanded={dateFilter.expanded}
          data-testid="crq-date-filter-toggle"
        >
          {dateFilter.expanded ? "− Hide date filter" : "+ Add date filter"}
        </button>
        {dateFilter.expanded && (
          <div className="crq-date-pair">
            <label className="crq-filter">
              <span className="crq-filter-label">From:</span>
              <input
                type="date"
                value={dateFilter.from}
                onChange={(e) =>
                  setDateFilter((d) => ({ ...d, from: e.target.value }))
                }
                className="crq-filter-date"
                data-testid="crq-date-from"
              />
            </label>
            <label className="crq-filter">
              <span className="crq-filter-label">To:</span>
              <input
                type="date"
                value={dateFilter.to}
                onChange={(e) =>
                  setDateFilter((d) => ({ ...d, to: e.target.value }))
                }
                className="crq-filter-date"
                data-testid="crq-date-to"
              />
            </label>
          </div>
        )}
      </div>

      {/* Body — table / loading / empty / error */}
      {loading ? (
        <div
          className="crq-loading"
          role="status"
          data-testid="crq-loading"
        >
          Loading correction requests...
        </div>
      ) : error ? (
        <div className="crq-error" role="alert" data-testid="crq-error">
          Couldn&apos;t load correction requests.{" "}
          <button
            type="button"
            className="crq-btn-link"
            onClick={fetchRequests}
            data-testid="crq-retry"
          >
            Retry
          </button>
        </div>
      ) : sortedRequests.length === 0 ? (
        <div
          className={`crq-empty crq-empty-${
            statusFilter === STATUS.PENDING ? "pending" : "all"
          }`}
          role="status"
          data-testid="crq-empty"
        >
          {statusFilter === STATUS.PENDING ? (
            <>
              <p className="crq-empty-headline">
                No pending corrections. You&apos;re all caught up.
              </p>
              <p className="crq-empty-detail">
                Staff requests will appear here as they&apos;re submitted.
              </p>
            </>
          ) : (
            <>
              <p className="crq-empty-headline">No correction requests yet.</p>
              <p className="crq-empty-detail">
                Once a staff member submits a correction, it&apos;ll appear here.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="crq-table-wrap">
          <table
            className="crq-table"
            data-testid="crq-table"
            aria-label="Correction requests"
          >
            <thead>
              <tr>
                <th scope="col">Staff</th>
                <th scope="col">Submitted</th>
                <th scope="col">Target date</th>
                <th scope="col">Intended times</th>
                <th scope="col">Reason</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRequests.map((r) => {
                const isCorrection = Boolean(r.targetTimeLogId);
                const target = formatTargetDate(r.targetDate);
                const isPending = r.status === STATUS.PENDING;
                return (
                  <tr
                    key={r._id}
                    className="crq-row"
                    onClick={(e) => handleRowClick(r, e)}
                    data-testid={`crq-row-${r._id}`}
                  >
                    <td>
                      <div className="crq-staff-cell">
                        <span className="crq-staff-name">{r.staffName}</span>
                        {r.staffRole && (
                          <span className="crq-staff-role">{r.staffRole}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="crq-cell-primary">
                        {formatRelative(r.createdAt)}
                      </div>
                      <div className="crq-cell-secondary">
                        {formatStudioDateTime(r.createdAt)}
                      </div>
                    </td>
                    <td>
                      <div className="crq-cell-primary">{target.primary}</div>
                      <div className="crq-cell-secondary">
                        {target.secondary}
                      </div>
                    </td>
                    <td>
                      {formatIntendedTimes(
                        r.intendedPunchInTime,
                        r.intendedPunchOutTime,
                        isCorrection
                      )}
                    </td>
                    <td>
                      <span
                        className="crq-reason-truncated"
                        title={r.reason}
                        aria-label={`Reason: ${r.reason}`}
                      >
                        {truncate(r.reason, 80)}
                      </span>
                    </td>
                    <td className="crq-actions-cell" data-no-row-click="true">
                      {isPending ? (
                        <>
                          <button
                            type="button"
                            className="crq-btn crq-btn-primary crq-btn-sm"
                            onClick={() => openApproveModal(r)}
                            data-testid={`crq-row-approve-${r._id}`}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="crq-btn crq-btn-secondary crq-btn-sm"
                            onClick={() => openRejectModal(r)}
                            data-testid={`crq-row-reject-${r._id}`}
                          >
                            Reject
                          </button>
                        </>
                      ) : (
                        <span className={`crq-pill crq-pill-${r.status?.toLowerCase()}`}>
                          {r.status}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ApproveModal
        open={approveModal.open}
        request={approveModal.request}
        staffName={
          approveModal.request &&
          (staffById.get(approveModal.request.requestedBy)?.name || "")
        }
        submitting={approveModal.submitting}
        bannerError={approveModal.bannerError}
        onCancel={closeApproveModal}
        onSubmit={handleApprove}
      />
      <RejectModal
        open={rejectModal.open}
        request={rejectModal.request}
        staffName={
          rejectModal.request &&
          (staffById.get(rejectModal.request.requestedBy)?.name || "")
        }
        submitting={rejectModal.submitting}
        bannerError={rejectModal.bannerError}
        onCancel={closeRejectModal}
        onSubmit={handleReject}
      />
    </div>
  );
};

export default CorrectionRequestQueue;
